import { useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useNotifications } from "features/notifications/hooks";
import { navStore } from "components/item-navigator";
import { GaiaConstants } from "utils/gaia-constants";

// Advances a completed draft review to the next reviewable draft in the queue
// registered by the drafts table. Falls back to the given path when the review
// was not opened from the queue (e.g. a deep link with no queue key).
export const useAdvanceToNextDraft = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { shortId } = useParams<{ shortId: string }>();
  const { addNotification } = useNotifications();
  const navKey = (location.state as { navKey?: string } | null)?.navKey;

  return useCallback(
    (fallbackPath: string) => {
      const navItems = navKey ? navStore.get(navKey) : null;
      const currentIndex =
        navItems?.findIndex((navItem) => navItem.id === shortId) ?? -1;
      const nextItem =
        navItems && currentIndex >= 0 ? navItems[currentIndex + 1] : undefined;
      if (nextItem) {
        navigate(`/drafts/${nextItem.id}`, { state: { navKey } });
      } else if (navKey) {
        // Opened from the queue but nothing left to review.
        addNotification({
          type: "info",
          header: "No more drafts to review",
          dismissible: true,
          timeout: GaiaConstants.NOTIFICATION_TIMEOUT.SHORT,
        });
        navigate("/drafts");
      } else {
        // Not opened from the drafts queue — keep the original destination.
        navigate(fallbackPath);
      }
    },
    [navKey, shortId, navigate, addNotification],
  );
};
